/***
 *
 * @file components/Label/index.tsx
 * @description Label component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Center
 * @since 13 Feb 2024
 *
 */

import React, { ReactNode } from "react";
// import "./Label.scss";
import { StyledLabel } from "@/shared/StyledComponents";

type T_Props = {
  id?: string,
  children: ReactNode,
  htmlFor?: string,
  required?: boolean,
  disabled?: boolean,
  className?: string,
  style?: any,
  data?: any
}

/**
 * Name: Label
 * 
 * Description: It renders the children (mostly an input field) enclosed inside of label component
 * @param param0 
 * @example Parameter breakup
 * ```
 *  param0: {
 *    children,         //to pass the children components to be rendered
 *    htmlFor,          //to pass id for the corresponding input field
 *    className: "",    //to provide a string including different classes
 *    style: {},        //to provide custom style object
 *    required: false,  //to provide validations
 *    disabled: false,  //used to disable a field
 *  }
 * ```
 * @example T_Props 
 * ```
 * type T_Props = {
 *     children: ReactNode,
 *     htmlFor?: string,      //optional
 *     id?: string,           //optional
 *     required?: boolean,    //optional
 *     disabled?: boolean,    //optional
 *     className?: string,    //optional
 *     style?: any,           //optional
 *     data?: any             //optional
 * }
 * ```
 * @returns a label component enclosing corresponding input field component
 */

const Label = ({ id="label", children, htmlFor, className = "", style = {}, required = false, disabled = false }: T_Props) => {

  return (
    <StyledLabel
      data-testid="Label"
      className={`${className} ${disabled ? 'text-muted' : ''}`}
      style={{ ...style }}>
      {children}{required ? <span className="pl-.5 text-red">*</span> : ""}
    </StyledLabel>
  );
};

export default Label;
